import { useFormContext, useController } from 'react-hook-form';
import { DatesType, FormType } from '../../../shared/models/form';
import Select from 'react-select';
import CustomMenuList from './CustomMenuList';
import { format } from 'date-fns';
import { getLocale } from '../utils';

interface DatesProps {
  isTripTypeRoundTrip: boolean;
  label: string;
  type: DatesType;
  fieldIndex: number;
  onDateSelect?: (fieldIndex: number) => void;
}

const roundedClass = (
  isTripTypeRoundTrip: boolean,
  type: DatesType,
  isFocused: boolean,
): string => {
  if (!isTripTypeRoundTrip) {
    if (isFocused) {
      return 'rounded-b-none';
    }
    return '';
  }

  if (isFocused) {
    return type === 'departure'
      ? 'rounded-b-none sm:rounded-r-none z-10'
      : 'sm:rounded-l-none rounded-b-none z-10';
  }

  return type === 'departure' ? 'sm:rounded-r-none' : 'sm:rounded-l-none sm:border-l-white';
};

const Dates = ({ isTripTypeRoundTrip, label, type, fieldIndex, onDateSelect }: DatesProps) => {
  const { control, setValue, setFocus, trigger } = useFormContext<FormType>();
  const {
    field: { onChange, onBlur, value, ref, name },
    fieldState: { error },
  } = useController({ control, name: `bounds.${fieldIndex}.${type}`, rules: { required: true } });

  const onRangeChange = (newDeparture: string, newReturn: string) => {
    setValue(`bounds.0.departure`, newDeparture);
    setValue('bounds.0.return', newReturn);
    if (type === 'departure') {
      void trigger(`bounds.0.departure`);
    }
    void trigger('bounds.0.return');

    if (document.activeElement instanceof HTMLInputElement) {
      document.activeElement.blur();
    }
    if (!newReturn) {
      setFocus('bounds.0.return');
    }
  };

  const displayDate =
    value && format(new Date(`${value}T00:00:00`), 'd MMM yyyy', { locale: getLocale() });

  return (
    <div>
      <label className="text-sm mb-2 block px-2">{label}</label>
      <Select
        ref={ref}
        onBlur={onBlur}
        name={name}
        menuShouldScrollIntoView={false}
        isSearchable={false}
        unstyled
        minMenuHeight={400}
        maxMenuHeight={400}
        placeholder={displayDate || label}
        openMenuOnFocus
        components={{
          MenuList: (menuListProps) =>
            CustomMenuList({
              menuListProps,
              isTripTypeRoundTrip,
              fieldIndex,
              onChange,
              onRangeChange,
              onDateSelect,
              type,
            }),
          IndicatorSeparator: null,
          DropdownIndicator: null,
        }}
        classNames={{
          control: (state) => {
            return `w-full h-12 bg-white border ${
              error && !state.menuIsOpen ? 'border-red-500' : 'border-input-gray'
            } px-4 focus:ring-2 rounded ${
              state.menuIsOpen
                ? `ring-2 ring-gray-500 ${roundedClass(isTripTypeRoundTrip, type, true)}`
                : roundedClass(isTripTypeRoundTrip, type, false)
            }`;
          },
          menuList: () =>
            `bg-white border-nok-gray rounded-b ring-2 ring-gray-500 mt-[1px] z-40 absolute ${
              type === 'departure' ? 'sm:left-0' : 'sm:left-[-100%]'
            } ${isTripTypeRoundTrip ? 'sm:w-[200%]' : 'sm:w-full'} flex justify-center`,
          placeholder: () => `${value ? 'text-black' : 'text-input-gray'}`,
        }}
      />
    </div>
  );
};

export default Dates;
